
// src/components/IncidentNotesModal.jsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Modal, Button, Form, Spinner, Badge } from 'react-bootstrap';
import { Clipboard, Download } from 'react-bootstrap-icons';
import axios from '../axiosAlertConfig';

const DEBUG = true;
const dbg = (...a) => DEBUG && console.log('[IncidentNotesModal]', ...a);

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return String(d); }
}
function fmtStampMMDDYY(d) {
  const dt = d ? new Date(d) : new Date();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  const yy = String(dt.getFullYear()).slice(-2);
  return `${mm}${dd}${yy}`;
}
function sanitizeFilename(s) {
  return String(s || 'Incident').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, ' ').trim();
}
function isAlertUser(user) {
  const r = (user?.userRole || '').toLowerCase().replace(/\s+/g, '');
  return r === 'alertuser';
}

export default function IncidentNotesModal({
  show,
  onHide,
  incident,     // { incidentID, incidentType, incidentCategory, createdAt, buildingName, incidentMode, messageText, iAccountID }
  onSaved,      // optional callback(newIncidentRow or note)
  user,         // ← pass UserContext.user here
}) {
  const [notes, setNotes]   = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty]   = useState(false);

  // copy helpers
  const [copied, setCopied] = useState(false);
  const textRef     = useRef(null);
  const copyTimerRef= useRef(null);

  const meIsAlertUser = isAlertUser(user);

  useEffect(() => () => { if (copyTimerRef.current) clearTimeout(copyTimerRef.current); }, []);

  const meta = useMemo(() => ({
    id:       incident?.incidentID,
    type:     incident?.incidentType || '—',
    category: incident?.incidentCategory || '—',
    when:     fmtDate(incident?.createdAt),
    place:    incident?.buildingName || incident?.locationName || '—',
    mode:     (incident?.incidentMode || '').toUpperCase() === 'DRILL' ? 'DRILL' : 'REAL',
  }), [incident]);

  // Load seed notes when modal opens:
  useEffect(() => {
    if (!show || !incident?.incidentID) return;

    if (meIsAlertUser) {
      // Shared field path (unchanged for AlertUser)
      const initial = incident?.messageText ?? '';
      setNotes(initial);
      setDirty(false);
      setCopied(false);
      dbg('open modal (shared)', {
        id: incident?.incidentID,
        type: meta.type,
        category: meta.category,
        when: incident?.createdAt,
        building: meta.place,
        initialLen: (initial ?? '').length
      });
      return;
    }

    // Per-user notes path
    (async () => {
      try {
        const { data } = await axios.get(`/ialert/incidents/${incident.incidentID}/notes`, {
          params: { userID: user?.userID, debug: 1 },
          headers: { 'X-User-Role': user?.userRole || '' }
        });
        const txt = data?.note?.notesText ?? '';
        setNotes(txt);
        setDirty(false);
        setCopied(false);
        dbg('open modal (personal)', {
          id: meta.id,
          type: meta.type, category: meta.category, when: incident?.createdAt,
          building: meta.place, initialLen: (txt ?? '').length
        });
      } catch (e) {
        console.error('Load personal note failed', e);
        // Fallback to empty; do NOT seed with shared text
        setNotes('');
        setDirty(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, incident?.incidentID]);

  const handleSave = async () => {
    if (!incident?.incidentID) return;

    try {
      setSaving(true);
      if (meIsAlertUser) {
        // Shared incident messageText (legacy behavior for AlertUser)
        dbg('saving… (shared)', {
          id: incident.incidentID,
          len: (notes ?? '').length,
          preview: String(notes ?? '').slice(0, 80)
        });
        const { data } = await axios.put(
          `/ialert/incidents/${incident.incidentID}`,
          { messageText: notes ?? '' },
          { params: { debug: 1 } }
        );
        dbg('save response (shared)', {
          id: data?.incidentID || incident.incidentID,
          matchesRequest: true,
          savedLen: (data?.messageText ?? '').length
        });
        setDirty(false);
        if (onSaved) onSaved(data);
      } else {
        // Personal per-user note
        dbg('saving… (personal)', {
          id: incident.incidentID,
          uid: user?.userID,
          len: (notes ?? '').length,
          preview: String(notes ?? '').slice(0, 80)
        });
        const { data } = await axios.put(
          `/ialert/incidents/${incident.incidentID}/notes`,
          {
            iAccountID: incident?.iAccountID,
            userID    : user?.userID,
            userName  : user?.userName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
            userRole  : user?.userRole || '',
            notesText : notes ?? ''
          },
          { params: { debug: 1 }, headers: { 'X-User-Role': user?.userRole || '' } }
        );
        dbg('save response (personal)', {
          id: data?.note?.incidentID || incident.incidentID,
          uid: data?.note?.userID,
          savedLen: (data?.note?.notesText ?? '').length
        });
        setDirty(false);
        // onSaved is optional; incident row didn't change for personal notes.
        if (onSaved) onSaved(data?.note);
      }
    } catch (e) {
      console.error('Save notes failed', e);
      alert('Failed to save notes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      const content = notes ?? '';
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else if (textRef.current) {
        const el = textRef.current; el.focus(); el.select();
        document.execCommand('copy');
        const len = el.value.length; el.setSelectionRange(len, len);
      } else { throw new Error('Clipboard API not available'); }
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed', err);
      alert('Unable to copy automatically. Please select the text and press Ctrl+C / Cmd+C.');
    }
  };

  const handleExport = () => {
    const stamp   = fmtStampMMDDYY(incident?.createdAt);
    const safeType= sanitizeFilename(incident?.incidentType || 'Incident');
    const filename= `${safeType}(${stamp}).txt`;
    const blob    = new Blob([String(notes ?? '')], { type: 'text/plain;charset=utf-8' });
    const url     = URL.createObjectURL(blob);
    const a       = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const labelText = meIsAlertUser ? 'Notes (shared with all AlertUsers – incident message)' : 'Your Notes (private to you)';

  return (
    <Modal show={show} onHide={onHide} fullscreen centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Incident Notes</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Meta header */}
        <div className="mb-3 p-2 border rounded" style={{ background: '#f7f7f7' }}>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div><strong>Type:</strong> {meta.type}</div>
            <div><strong>Category:</strong> {meta.category}</div>
            <div><strong>Date:</strong> {meta.when}</div>
            <div><strong>Campus/Building:</strong> {meta.place}</div>
            <div>
              <strong>Mode:</strong>{' '}
              <Badge bg={meta.mode === 'DRILL' ? 'success' : 'primary'}>{meta.mode}</Badge>
            </div>
          </div>
        </div>

        {/* Notes editor */}
        <Form.Group controlId="incidentNotes">
          <div className="d-flex align-items-center justify-content-between">
            <Form.Label className="fw-semibold m-0">{labelText}</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExport}
                title="Export notes to a text file"
                aria-label="Export notes to a text file"
              >
                <Download size={14} className="me-1" />
                Export Notes
              </Button>
              <Button
                size="sm"
                variant={copied ? 'success' : 'outline-secondary'}
                onClick={handleCopy}
                title="Copy notes to clipboard"
                aria-label="Copy notes to clipboard"
              >
                <Clipboard size={14} className="me-1" />
                {copied ? 'Copied!' : 'Copy Notes'}
              </Button>
            </div>
          </div>

          <Form.Control
            as="textarea"
            rows={14}
            ref={textRef}
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
            placeholder="Enter incident notes here…"
            style={{ resize: 'vertical' }}
          />
          {meIsAlertUser ? (
            <div className="form-text">
              These notes write to this incident’s shared <code>messageText</code> field.
            </div>
          ) : (
            <div className="form-text">
              These notes are saved privately for your user in <code>iAlertIncidentUserNotes</code>.
            </div>
          )}
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !dirty}
          title={!dirty ? 'No changes to save' : 'Save notes'}
        >
          {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
          Save Notes
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

 